import { useQuery } from '@tanstack/react-query';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { DetailTableLayout } from '@/components/common/DetailTableLayout';
import { useRememberViewedRow } from '@/hooks/useLastViewedRow';
import { getInstituteHandOutStatCards } from '@/utils/handOutStatCards';
import { promoInstitutesQueryOptions, promoMembersQueryOptions } from '../api/promotion.api';
import { getPromoInstituteColumns } from '../components/PromoColumns';
import { usePromoAdjustmentNavigation } from '../hooks/usePromoAdjustmentNavigation';
import type { PromoAdjustmentUnit, PromoDrillParams } from '../types/promotion.types';

const UNKNOWN_ZM_TITLE = 'ZM';

const INSTITUTE_COLUMNS = getPromoInstituteColumns();

interface PromoItemZmInstitutesPageProps {
  unit: PromoAdjustmentUnit;
  promoItemId: number;
  zmId: number;
  zmSearch: PromoDrillParams;
}

export function PromoItemZmInstitutesPage({
  unit,
  promoItemId,
  zmId,
  zmSearch,
}: PromoItemZmInstitutesPageProps) {
  const { openItemZms } = usePromoAdjustmentNavigation(unit);
  useRememberViewedRow('promoItemZms', zmId, promoItemId);

  const zmsQuery = useQuery(promoMembersQueryOptions(promoItemId, zmSearch));
  const institutesQuery = useQuery(promoInstitutesQueryOptions(promoItemId, zmId, zmSearch));

  const zm = zmsQuery.data?.find((member) => member.userId === zmId);
  const title = zmsQuery.isLoading ? undefined : (zm?.name ?? UNKNOWN_ZM_TITLE);

  return (
    <DetailTableLayout
      title={title}
      titleAddon={<DateRangeBadge dateFrom={zmSearch.dateFrom} dateTo={zmSearch.dateTo} />}
      onNoHistory={() => openItemZms(promoItemId, zmSearch)}
      stats={getInstituteHandOutStatCards(institutesQuery.data)}
      table={{
        rows: institutesQuery.data ?? [],
        columns: INSTITUTE_COLUMNS,
        isLoading: institutesQuery.isLoading,
        error: institutesQuery.error,
        errorTitle: 'Could not load the institutes',
        onRetry: () => void institutesQuery.refetch(),
        stickyColumnId: 'instituteName',
        emptyMessage: 'No entry carries this item',
      }}
    />
  );
}
